import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../services/auth';
import { getUserDeletionImpact } from '../services/api';
import DeleteImpactModal from '../components/DeleteImpactModal';
import { safeErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../App';
import Modal from '../components/Modal';
import {
  HiOutlineUserAdd, HiOutlinePencil, HiOutlineTrash,
  HiOutlineShieldCheck, HiOutlineShieldExclamation,
  HiOutlineUserGroup
} from 'react-icons/hi';

const emptyForm = { email: '', username: '', password: '', full_name: '', role: 'utilisateur' };

export default function UserManagement() {
  const addToast = useToast();
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await getUsers();
      setUsers(data);
    } catch {
      addToast('error', 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email,
      username: u.username,
      password: '',
      full_name: u.full_name,
      role: u.role,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await updateUser(editingUser.id, payload);
        addToast('success', 'Utilisateur modifie avec succes');
      } else {
        await createUser(form);
        addToast('success', 'Utilisateur cree avec succes');
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      const detail = safeErrorMessage(err, 'Erreur');
      addToast('error', detail);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      addToast('success', `${u.full_name || u.username} ${u.is_active ? 'desactive' : 'active'}`);
      loadUsers();
    } catch (err) {
      addToast('error', safeErrorMessage(err, 'Erreur'));
    }
  };

  const handleDelete = async (impact) => {
    if (!confirmDelete) return;
    try {
      const { data } = await deleteUser(confirmDelete.id);
      const detail = (data?.impact || impact?.consequences || []).join(' ');
      addToast(
        'success',
        detail
          ? `Compte « ${confirmDelete.username} » supprimé. ${detail}`
          : `Compte « ${confirmDelete.username} » supprimé.`,
        'Suppression effectuée',
      );
      setConfirmDelete(null);
      loadUsers();
    } catch (err) {
      // 409 : le serveur refuse et explique pourquoi, ce n'est pas une panne
      addToast('error', safeErrorMessage(err, 'Suppression impossible'), 'Suppression refusée');
    }
  };

  const getInitials = (u) => {
    if (u.full_name) {
      return u.full_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    }
    return u.username.slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <output className="spinner-border" />
        <span>Chargement...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h2>
          <HiOutlineUserGroup style={{ marginRight: 8 }} />
          Gestion des Utilisateurs
        </h2>
        <div className="actions">
          <button className="btn btn-success btn-sm" onClick={openCreate}>
            <HiOutlineUserAdd style={{ marginRight: 4 }} />
            Nouvel utilisateur
          </button>
        </div>
      </div>
      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Nom complet</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Statut</th>
                  <th style={{ width: 140 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                    <td>
                      <div className="sidebar-user-avatar" style={{ width: 30, height: 30, fontSize: '0.65rem' }}>
                        {getInitials(u)}
                      </div>
                    </td>
                    <td className="fw-500">{u.full_name || '-'}</td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>{u.email}</td>
                    <td style={{ fontSize: '0.82rem' }}>{u.username}</td>
                    <td>
                      <span className={`user-role-badge user-role-${u.role}`}>
                        {{ superadmin: 'Super admin', admin: 'Admin' }[u.role] || 'Utilisateur'}
                      </span>
                    </td>
                    <td>
                      <span className={`user-status-badge ${u.is_active ? 'user-status-active' : 'user-status-inactive'}`}>
                        {u.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => openEdit(u)}
                          title="Modifier"
                        >
                          <HiOutlinePencil />
                        </button>
                        <button
                          className={`btn btn-sm ${u.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => handleToggleActive(u)}
                          title={u.is_active ? 'Desactiver' : 'Activer'}
                          disabled={u.id === currentUser?.id}
                        >
                          {u.is_active ? <HiOutlineShieldExclamation /> : <HiOutlineShieldCheck />}
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setConfirmDelete(u)}
                          title="Supprimer"
                          disabled={u.id === currentUser?.id}
                        >
                          <HiOutlineTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        icon={editingUser ? <HiOutlinePencil /> : <HiOutlineUserAdd />}
        footer={
          <>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>
              Annuler
            </button>
            <button className="btn btn-success btn-sm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Enregistrement...' : (editingUser ? 'Modifier' : 'Creer')}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-user-full-name">Nom complet</label>
            <input
              id="field-user-full-name"
              type="text"
              className="form-control"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="Jean Dupont"
            />
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-user-email">Email</label>
              <input
                id="field-user-email"
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jean@alton.local"
                required
              />
            </div>
            <div className="col-6">
              <label className="form-label" htmlFor="field-user-username">Nom d'utilisateur</label>
              <input
                id="field-user-username"
                type="text"
                className="form-control"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="jean.dupont"
                required
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <label className="form-label" htmlFor="field-user-password">
                Mot de passe {editingUser && <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>(laisser vide pour ne pas changer)</span>}
              </label>
              <input
                id="field-user-password"
                type="password"
                className="form-control"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                required={!editingUser}
                minLength={8}
              />
            </div>
            <div className="col-6">
              <label className="form-label" htmlFor="field-user-role">Role</label>
              <select
                id="field-user-role"
                className="form-select"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="utilisateur">Utilisateur</option>
                {isSuperAdmin && <option value="admin">Administrateur</option>}
              </select>
            </div>
          </div>
        </form>
      </Modal>

      {/* Suppression : impact analyse par le serveur avant toute action */}
      <DeleteImpactModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le compte"
        targetLabel={`${confirmDelete?.full_name || confirmDelete?.username || ''} (${confirmDelete?.role || ''})`}
        fetchImpact={() => getUserDeletionImpact(confirmDelete.id)}
        confirmText="Supprimer le compte"
      />
    </div>
  );
}
